import React, { useState, useEffect } from 'react';
import {
    CardContent, CardActions,
    TextField, Button, Typography,
    Box, Dialog,
    DialogContent,
    DialogActions,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { financialService } from '../utils/api';

const KYCLandingCard = ({ onChange }) => {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('+91 ');
    const [email, setEmail] = useState('');
    const [nameError, setNameError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validatePhone = (value) => {
        if (!value.startsWith('+91 ')) {
            return 'Phone number must start with +91';
        } else if (value.length < 14) {
            return 'Phone number must be 10 digits after country code';
        } else if (!/^\+91 \d{10}$/.test(value)) {
            return 'Please enter a valid 10-digit phone number';
        }
        return '';
    };

    const handlePhoneChange = (e) => {
        let value = e.target.value;
        
        // Ensure it always starts with +91
        if (!value.startsWith('+91 ')) {
            value = '+91 ' + value.replace('+91 ', '');
        }
        
        // Restrict to only numbers after +91
        const phoneRegex = /^\+91 \d*$/;
        if (value.length > 4 && !phoneRegex.test(value)) {
            return; // Don't update if invalid characters entered
        }
        
        // Limit to country code + 10 digits
        if (value.length <= 14) {
            setPhone(value);
            // Clear phone error when user types
            if (phoneError) {
                setPhoneError('');
            }
        }
    };

    const validateForm = () => {
        let isValid = true;

        // Name validation
        if (!name.trim()) {
            setNameError('Name is required');
            isValid = false;
        } else if (!/^[a-zA-Z\s.'-]{2,50}$/.test(name.trim())) {
            setNameError('Name contains invalid characters');
            isValid = false;
        } else {
            setNameError('');
        }

        // Phone validation - MANDATORY
        const phoneValidationError = validatePhone(phone);
        if (phoneValidationError) {
            setPhoneError(phoneValidationError);
            isValid = false;
        } else {
            setPhoneError('');
        }

        // Email validation
        const emailRegex = /^[\w-.]+@[\w-]+\.[\w-.]+$/;
        if (!email.trim()) {
            setEmailError('Email is required');
            isValid = false;
        } else if (!emailRegex.test(email)) {
            setEmailError('Please enter a valid email address');
            isValid = false;
        } else {
            setEmailError('');
        }

        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);
        
        try {
            const phoneNumber = phone.split(" ")[1]; // Extract only the 10-digit number
            const response = await financialService.sendOTP({ "phone_number": phoneNumber });
            
            if (response.status_code === 200) {
                setPhoneError('');
                setInfoDialogOpen(false);
                // Pass the complete user data to parent
                onChange('proceed', { 
                    name: name.trim(), 
                    phoneNo: phoneNumber, 
                    email: email.trim() 
                });
            } else {
                setPhoneError(response.message || 'Failed to send OTP. Please try again.');
            }
        } catch (error) {
            console.error("Error while sending OTP:", error);
            setPhoneError('Failed to send OTP. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
            setInfoDialogOpen(false);
        }
    };

    const handleInfoDialogOpen = () => {
        if (validateForm()) {
            setInfoDialogOpen(true);
        }
    };

    const handleInfoDialogClose = () => {
        setInfoDialogOpen(false);
        setIsSubmitting(false);
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (infoDialogOpen) {
              handleSubmit();   // dialog open → submit directly
            } else {
              handleInfoDialogOpen(); // dialog closed → open it
            }
          }
        };
    
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
      }, [infoDialogOpen, name, phone, email]); // include deps if needed

    return (
        <Box>
            <CardContent>
                <Typography variant="h6" sx={{ mb: 4 }}>
                    How secured are your financial goals and retirement? <br />
                    Find out in under 5 minutes.<br />
                </Typography>

                <TextField
                    label="Full Name"
                    variant="outlined"
                    fullWidth
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        if (nameError) setNameError('');
                    }}
                    error={!!nameError}
                    helperText={nameError}
                    sx={{ mb: 2 }}
                    required
                />

                <TextField
                    label="Phone Number"
                    variant="outlined"
                    fullWidth
                    value={phone}
                    onChange={handlePhoneChange}
                    type="tel"
                    inputMode="numeric"
                    error={!!phoneError}
                    helperText={phoneError}
                    sx={{ mb: 2 }}
                    required
                />

                <TextField
                    label="Email"
                    variant="outlined"
                    fullWidth
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError('');
                    }}
                    error={!!emailError}
                    helperText={emailError}
                    sx={{ mb: 5 }}
                    required
                />
                
                <CardActions sx={{ pt: 1 }}>
                    <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        size="large"
                        onClick={handleInfoDialogOpen}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            borderRadius: 1,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 'bold'
                        }}
                        disabled={isSubmitting}
                    >
                        PROCEED
                    </Button>
                </CardActions>
            </CardContent>

            <Dialog
                open={infoDialogOpen}
                onClose={handleInfoDialogClose}
                aria-labelledby="info-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogContent
                    sx={{
                        maxHeight: '400px',
                        overflowY: 'scroll',
                        scrollbarWidth: 'auto',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#888',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                        },
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        To help assess your goals and asset allocation, we request your consent to process your personal data. This includes your personal and financial details such as name, age, contact details, financial net-worth, financial goals, and risk preferences and other relevant information. You may withdraw your consent at any time by writing to us at clientsupport@marcellus.in.
                        <br /><br />
                        For any grievance, you may write to us at grievances@marcellus.in or escalate a complaint to the Data protection Board.
                        <br /><br />
                        Please note that this service provides a goal planning and asset allocation report. It is not a comprehensive financial plan or personalized investment advice. All calculations and estimations are indicative, and we recommend that you independently verify them before making any investment decisions.
                        <br /><br />
                        By clicking on "Proceed", you consent to Marcellus Investment Managers Private Limited to process your personal data as described above. If you do not wish to proceed, you may click 'Cancel'.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={handleInfoDialogClose} 
                        color="inherit"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        variant="contained" 
                        color="success"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Sending OTP...' : 'Proceed'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KYCLandingCard;